import { useState, useEffect } from 'react'
import { PageType, SearchItem } from '../types'
import { getNavigationPath } from '../getNavigationPath'
import { NewsIcon, NavigatorIcon, HerdPowerIcon, DecoderIcon } from 'assets/icons/icons'

// Typesense configuration
const TYPESENSE_HOST = process.env.REACT_APP_TYPESENSE_HOST
const TYPESENSE_PORT = '443'
const TYPESENSE_PROTOCOL = 'https'
const TYPESENSE_API_KEY = process.env.REACT_APP_TYPESENSE_API_KEY

interface TypesenseDocument {
	aliases: string[]
	company: string
	decoder: number
	herd: number
	id: string
	lo1: string
	news: number
	page_type: PageType
	search_blob: string
	sector: string
	ticker: string
	ticker_id: number
}

interface TypesenseHighlight {
	field: string
	matched_tokens: string[]
	snippet: string
}

interface TypesenseHit {
	document: TypesenseDocument
	highlight?: {
		ticker?: {
			matched_tokens: string[]
			snippet: string
		}
	}
	highlights: TypesenseHighlight[]
	text_match: number
	text_match_info: {
		best_field_score: string
		best_field_weight: number
		fields_matched: number
		num_tokens_dropped: number
		score: string
		tokens_matched: number
		typo_prefix_score: number
	}
}

interface TypesenseResponse {
	facet_counts: any[]
	found: number
	hits: TypesenseHit[]
}

export const useSearchResults = (query: string) => {
	const [searchResults, setSearchResults] = useState<SearchItem[]>([])
	const [isSearching, setIsSearching] = useState(false)
	const [isLoadingData, setIsLoadingData] = useState(false)

	useEffect(() => {
		if (!query.trim()) {
			setSearchResults([])
			setIsSearching(false)
			return
		}

		setIsSearching(true)
		setIsLoadingData(true)

		// Fetch from Typesense
		const url = `${TYPESENSE_PROTOCOL}://${TYPESENSE_HOST}:${TYPESENSE_PORT}/collections/mavbots_helix/documents/search`
		const params = new URLSearchParams({
			q: query,
			query_by: 'search_blob,ticker,company,aliases',
			per_page: '50',
			sort_by: '_text_match:desc, priority:asc'
		})

		fetch(`${url}?${params.toString()}`, {
			headers: {
				'X-TYPESENSE-API-KEY': TYPESENSE_API_KEY || '',
				'Content-Type': 'application/json'
			}
		})
			.then(async res => {
				if (!res.ok) throw new Error(`Typesense error: ${res.status}`)
				return res.json() as Promise<TypesenseResponse>
			})
			.then(data => {
				// Transform Typesense hits into SearchItems
				const transformedResults = data.hits.map(hit => {
					const doc = hit.document

					// Use the getNavigationPath function
					const navigationPath = getNavigationPath(doc.page_type, doc.ticker, doc.company)

					// Determine icon based on page_type
					let icon = undefined
					if (['other', 'summary', 'earnings', 'metrics', 'dynamics', 'statements'].includes(doc.page_type)) {
						icon = DecoderIcon
					} else if (doc.page_type === 'news') {
						icon = NewsIcon
					} else if (doc.page_type === 'herdpower') {
						icon = HerdPowerIcon
					} else if (doc.page_type.includes('navigator')) {
						icon = NavigatorIcon
					}

					// Create base item
					const item: SearchItem = {
						id: doc.id,
						label:
							doc.page_type === 'feature' || doc.page_type === 'bot'
								? doc.company
								: ['other', 'summary', 'earnings', 'metrics', 'dynamics', 'statements', 'herdpower', 'news'].includes(
										doc.page_type
								  )
								? `${doc.company} (${doc.ticker}) - ${doc.page_type.charAt(0).toUpperCase() + doc.page_type.slice(1)}`
								: `${doc.company} (${doc.ticker})`,
						type: doc.page_type,
						to: navigationPath,
						icon,
						image: doc.page_type === 'stock' || doc.page_type === 'etf' || doc.page_type === 'bot' ? doc.lo1 : '',
						keywords: doc.aliases,
						section: doc.sector
					}

					return item
				})

				// Add single group header for all results
				const groupedResults = [
					{
						id: 'group-header-results',
						label: 'Results',
						type: 'GroupHeader' as any,
						isGroupHeader: true
					},
					...transformedResults
				]

				setSearchResults(groupedResults)
				setIsSearching(false)
				setIsLoadingData(false)
			})
			.catch(err => {
				console.error('Search failed:', err)
				setSearchResults([])
				setIsSearching(false)
				setIsLoadingData(false)
			})
	}, [query])

	return {
		searchResults,
		isSearching,
		isLoadingData
	}
}
